import { SearchResponseEntry } from '../../types/global'; // eslint-disable-line import/extensions
import store from '../global/store';
// import LatLng from '@googlemaps/js-api-loader'

interface LocationObject {
  lat: number;
  lng: number;
}

class _LocalStorageService {
  /**
   * True if localStorage is supported AND enabled, false otherwise.
   */
  isLocalStorageEnabled = this.detectStorageEnabled('localStorage');

  /**
   * Detect if localStorage or sessionStorage is supported AND enabled.
   * Browsers may expose the localStorage object, but usage might be disabled.
   * This function returns true if storage is supported AND enabled.
   *
   * @author https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
   *
   * @param {('localStorage'|'sessionStorage')} type - type of storage to detect
   * @returns {boolean} true if storage type is supported AND enabled, false otherwise.
   */
  detectStorageEnabled(type): boolean {
    let storage;
    try {
      storage = window[type];
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  }

  /**
   * Save search term, location, and selected bank to localStorage.
   */
  saveSearchValues() {
    if (!this.isLocalStorageEnabled) return;
    if (store.state.isCurrentlyRestoring) return;

    const searchValues = {
      term: store.state.selectedPlaceName,
      location: store.state.selectedPlaceLocation,
      selectedBank: store.state.selectedBank,
      activeFilterValue: store.state.activeFilterValue,
    };

    localStorage.setItem('bankSearchValues', JSON.stringify(searchValues));
  }

  /**
   * Restore search term, location, and selected bank from localStorage.
   */
  restoreSearchValues() {
    store.state.isRestoredDataFromLocalstorage = false;

    if (!this.isLocalStorageEnabled) return;

    const storageObject = JSON.parse(localStorage.getItem('bankSearchValues'));

    if (!storageObject) return;

    store.state.isRestoredDataFromLocalstorage = true;

    const restoredValues = {
      term: storageObject.term as string,
      location: this.restoreLatLngFromObject(
        storageObject.location as LocationObject
      ),
      selectedBank: storageObject.selectedBank as SearchResponseEntry,
      activeFilterValue: storageObject.activeFilterValue as string,
    };

    store.state.isCurrentlyRestoring = true;
    store.state.selectedPlaceName = restoredValues.term;
    store.state.selectedPlaceLocation = restoredValues.location;
    store.state.selectedBank = restoredValues.selectedBank;
    store.state.isCurrentlyRestoring = false;
    store.state.activeFilterValue = restoredValues.activeFilterValue;
  }

  /**
   * Converts a plain {"lat": 50.1130405, "lng": 8.6965489} object into a google.maps.LatLng
   * object, for which lat and lng aren't floats, but functions returning a float.
   */
  restoreLatLngFromObject(locationObject: LocationObject): google.maps.LatLng {
    if (!locationObject) return null;
    return new google.maps.LatLng(locationObject.lat, locationObject.lng);
  }

  /**
   * Remove search term, location, and selected bank from localStorage.
   */
  removeSearchValues() {
    if (!this.isLocalStorageEnabled) return;

    localStorage.removeItem('bankSearchValues');
  }
}

export const LocalStorageService = new _LocalStorageService();
